import type { VideoPlayer } from '@zoom/videosdk';

import { logger } from '../../datadog/logger';
import { Browser } from '../../types';
import { detectBrowser, trySafely } from '../../utils';

const isSafari = detectBrowser() === Browser.Safari;

type VideoElementResolver = (element: HTMLVideoElement | VideoPlayer) => HTMLVideoElement | null;

/**
 * Shared Picture-in-Picture controller parameterized by a video element resolver.
 * - Twilio element refs point directly at a `<video>`
 * - Zoom element refs point at a `<video-player>` whose `<video>` descendant must be queried.
 *   - Only true in Chrome and Safari, not true for other browsers.
 */
export class PictureInPicture {
  #resolveVideo: VideoElementResolver;
  #activeVideo: HTMLVideoElement | null;

  constructor(resolveVideo: VideoElementResolver) {
    this.#resolveVideo = resolveVideo;
    this.#activeVideo = null;
  }

  get isSupported() {
    return !!document.pictureInPictureEnabled;
  }

  onVideoReady(element: HTMLVideoElement | VideoPlayer, callback: () => void): () => void {
    const video = this.#resolveVideo(element);
    if (!video) return () => {};

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      callback();
      return () => {};
    }

    const abortController = new AbortController();
    video.addEventListener('loadedmetadata', callback, {
      signal: abortController.signal,
    });
    return () => abortController.abort();
  }

  async enter(element: HTMLVideoElement | VideoPlayer) {
    const video = this.#resolveVideo(element);
    if (!video) {
      logger.warn('Could not resolve <video> element for picture-in-picture', { element: element.tagName });
      return;
    }

    // Safari rejects requestPictureInPicture for video elements that weren't
    // initially played via a user gesture (e.g. Zoom SDK's internal <video>).
    // The webkit presentation mode API doesn't have this per-element restriction.
    if (isSafari) {
      // @ts-expect-error webkitSetPresentationMode is Safari-specific
      video.webkitSetPresentationMode('picture-in-picture');
    } else {
      await video.requestPictureInPicture();
    }

    this.#activeVideo = video;
  }

  exit() {
    if (isSafari) {
      // @ts-expect-error webkitSetPresentationMode is Safari-specific
      this.#activeVideo?.webkitSetPresentationMode?.('inline');
    } else if (document.pictureInPictureElement) {
      trySafely(document.exitPictureInPicture(), (error) => {
        logger.warn('Failed to exit picture-in-picture', {}, error);
      });
    }

    this.#activeVideo = null;
  }
}
